import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import geobuf from 'geobuf';
import Pbf from 'pbf';
import { Layer, Source } from 'react-map-gl/mapbox';
import { useMediaQuery } from 'react-responsive';
import { preferredWaveHeightUnit } from '../../../utils';

const s3UrlBase = 'https://seagull-visualization-layers.s3.us-east-2.amazonaws.com';

const WaveMagnitude = (props) => {
  const {
    UTCPath,
    selectedLake,
    isSharing,
  } = props;

  const isMobile = useMediaQuery({ maxWidth: 767 }) && !isSharing;
  const [isLoading, setIsLoading] = useState(false);
  const [waveLayers, setWaveLayers] = useState([]);

  const fetchOfs = async (ofs) => {
    const ofsUrl = `${s3UrlBase}${UTCPath}/${ofs}-waves-500m.pbf`;
    const ofsResponse = await fetch(ofsUrl);
    const ofsData = await ofsResponse.arrayBuffer();
    return geobuf.decode(new Pbf(ofsData));
  };

  const fetchData = async (selectedLake) => {
    if (selectedLake.length > 0) {
      const ofs = `l${selectedLake[0].toLowerCase()}`;
      const ofsData = await fetchOfs(ofs);
      setWaveLayers([
        {
          lake: 'primaryLake',
          sourceId: 'primaryLakeSource',
          data: ofsData,
        },
      ]);
    } else if (selectedLake.length === 0) {
      const [leofsData, lhofsData, lmofsData, loofsData, lsofsData] = await Promise.all([
        fetchOfs('le'),
        fetchOfs('lh'),
        fetchOfs('lm'),
        fetchOfs('lo'),
        fetchOfs('ls'),
      ]);
      setWaveLayers([
        {
          lake: 'primaryLake',
          sourceId: 'primaryLakeSource',
          data: leofsData,
        },
        {
          lake: 'lhofs',
          sourceId: 'lhofsSource',
          data: lhofsData,
        },
        {
          lake: 'lmofs',
          sourceId: 'lmofsSource',
          data: lmofsData,
        },
        {
          lake: 'loofs',
          sourceId: 'loofsSource',
          data: loofsData,
        },
        {
          lake: 'lsofs',
          sourceId: 'lsofsSource',
          data: lsofsData,
        },
      ]);
    } setIsLoading(false);
  };

  useEffect(() => {
    if (!isLoading) {
      setIsLoading(true);
      fetchData(selectedLake)
        .then(() => {
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }, [selectedLake, UTCPath]);

  const waveStyle = {
    type: 'fill',
    beforeId: 'waterway-label',
    paint: {
      'fill-color':
        [
          'interpolate',
          [
            'cubic-bezier',
            1,
            1,
            0.4,
            0.7,
          ],
          ['get', 'level'],
          0,
          '#1693a5',
          1,
          '#00d4b0',
          2,
          'hsl(88, 92%, 45%)',
          3,
          'hsl(73, 91%, 49%)',
          4,
          '#fffe0c',
          5,
          '#fbbe1a',
          6,
          '#F67D28',
          7,
          '#ff0026',
          8,
          '#e81e76',
          9,
          '#ac0606',
          10,
          '#720202',
        ],
      'fill-opacity': 1,
      'fill-antialias': true,
    },
  };
  const waveRange = isMobile
    ? [
      10,
      7.5,
      6,
      4.5,
      3,
      1.5,
      0,
    ]
    : [
      10,
      9,
      8,
      7,
      6,
      5,
      4,
      3,
      2,
      1,
      0,
    ];
  return (
    <>
      {isMobile
      && (
        <div className="mobile-temp-legend-container">
          <div className="mobile-wave-legend">
            {waveRange.map((waveRange, index) => (
              <div className="mobile-temp-legend-item" key={index}>
                <span className="mobile-temp-legend-item-text">
                  {`${preferredWaveHeightUnit(waveRange)}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {waveLayers.map((waveLayer, index) => (
        <Source
          id={waveLayer.sourceId}
          type="geojson"
          data={waveLayer.data}
          key={index}
        />
      ))}
      {waveLayers.map((waveLayer, index) => (
        <Layer
          id={waveLayer.lake}
          source={waveLayer.sourceId}
          {...waveStyle}
          key={index}
        />
      ))}
    </>
  );
};

WaveMagnitude.propTypes = {
  UTCPath: PropTypes.string,
  selectedLake: PropTypes.string,
  isSharing: PropTypes.bool,
};

WaveMagnitude.defaultProps = {
  UTCPath: '',
  selectedLake: '',
  isSharing: false,
};

export default WaveMagnitude;
